/** Display formatting shared across pages. */

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/** `1.4 MB`. Uses 1024 steps, matching what an OS file listing reports. */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) {
    return '0 B';
  }

  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), SIZE_UNITS.length - 1);
  const value = bytes / Math.pow(1024, exponent);

  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${SIZE_UNITS[exponent] ?? 'B'}`;
}

/** Thousands separators, so a dashboard figure stays readable at a glance. */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-GB').format(value);
}

/** `TENANT_CREATED` -> `Tenant created`, for audit rows and filter menus. */
export function humaniseConstant(value: string): string {
  const spaced = value.replace(/_/g, ' ').toLowerCase();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Initials for an avatar: "ABC Financial Services" -> "AF". */
export function initialsOf(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return '?';
  }
  if (words.length === 1) {
    return (words[0] ?? '').slice(0, 2).toUpperCase();
  }

  return `${words[0]?.charAt(0) ?? ''}${words[1]?.charAt(0) ?? ''}`.toUpperCase();
}

/* ------------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------------ */

/**
 * Every amount from the API is an integer number of minor units.
 *
 * 1250 AED arrives as 125000 fils. The server stores and sends it that way
 * because money in a floating point number is wrong the moment it is divided,
 * and a payroll run divides constantly - so this file is the only place in the
 * client that knows how to turn those integers back into something a person
 * reads, and how to turn what they type back into one.
 */
const MINOR_UNITS_PER_MAJOR = 100;

/** `AED 1,250.00`. The code is the tenant's own, from their HR settings. */
export function formatMoney(minorUnits: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
  }).format(minorUnits / MINOR_UNITS_PER_MAJOR);
}

/** `1,250.00` - the same figure without the code, for a column of amounts. */
export function formatAmount(minorUnits: number): string {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / MINOR_UNITS_PER_MAJOR);
}

/**
 * What goes in a number input: major units, unpadded.
 *
 * Not `formatAmount` - thousands separators in an input make the value
 * unparseable the moment the field is read back.
 */
export function toMajorUnits(minorUnits: number): number {
  return minorUnits / MINOR_UNITS_PER_MAJOR;
}

/**
 * What the user typed, back into minor units.
 *
 * Rounded rather than truncated, and rounded *after* multiplying: 12.34 is not
 * exactly representable, so `12.34 * 100` is 1233.9999999999998 and truncating
 * would quietly lose a fil on a third of all amounts.
 */
export function toMinorUnits(majorUnits: number): number {
  return Math.round(majorUnits * MINOR_UNITS_PER_MAJOR);
}

/**
 * `2.5 days`, `1 day`, `0.5 days`.
 *
 * Leave is counted in halves, so the fraction is shown when there is one and
 * hidden when there is not - "5.0 days" reads like a measurement rather than a
 * count of days off.
 */
export function formatDays(days: number): string {
  const rendered = Number.isInteger(days) ? String(days) : days.toFixed(1);

  return `${rendered} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * How a leave type's pay rate reads.
 *
 * The common cases are the words people use - paid, unpaid - and anything in
 * between falls back to the percentage rather than being rounded into one of
 * them, because half-pay sick leave is a real arrangement and calling it
 * "paid" would be wrong on the payslip.
 */
export function formatPayRate(payPercent: number): string {
  if (payPercent >= 100) {
    return 'Paid';
  }
  if (payPercent <= 0) {
    return 'Unpaid';
  }

  return `${String(payPercent)}% paid`;
}
